import XlsDownload from "@dana/Icons/XlsDownload";
import { elevation, themeColors } from "@dana/MobiusOverrides/styles/colors";
import Icon from "@insite/mobius/Icon";
import * as XLSX from "xlsx-js-style";
import React, { forwardRef } from "react";
import styled from "styled-components";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDateWithDash, getRegionSpecificDate } from "@dana/Common/utils";
import translate from "@insite/client-framework/Translate";
import { EMEARegion } from "@dana/Common/Constants";
import { formatDecimal } from "@dana/utils/utils";

type InvoiceExcelDownloadButtonProps = {
    invoice?: InvoiceDetailsResponse | null;
    totalAmt?: number;
    totalDiscount?: number;
    totalCertiCost?: number;
    totalPrice?: number;
    fileName?: string;
    sheetName?: string;
    label?: string;
    size?: number;
    disabled?: boolean;
    className?: string;
    selectedRegion?: string;
    onBeforeDownload?: () => void | Promise<void>;
    onAfterDownload?: () => void;
};

const StyledExcelDownloadButton = styled.button`
    padding: 4px;
    background-color: transparent;
    border: none;
    cursor: pointer;

    &:hover {
        color: ${themeColors.secondary.danaDarkBlue};
    }

    &:focus-visible {
        color: ${themeColors.secondary.danaDarkBlue};
        border: 1px solid ${themeColors.primary.white};
        box-shadow: ${elevation.focus};
    }

    @media print {
        display: none;
    }
`;

const NUM_FMT = "0.00";

function applyAlignment(ws: XLSX.WorkSheet, cellAddress: string, horizontal: "right" | "left" | "center" = "right") {
    if (ws[cellAddress]) {
        ws[cellAddress].s = {
            ...(ws[cellAddress].s ?? {}),
            alignment: { horizontal },
        };
    }
}

function applyNumberFormat(ws: XLSX.WorkSheet, cellAddress: string) {
    if (ws[cellAddress]) {
        ws[cellAddress].z = NUM_FMT;
        applyAlignment(ws, cellAddress, "right");
    }
}

function buildInvoiceWorkbook(invoice: InvoiceDetailsResponse | null,
    totalAmt: number,
    totalDiscount: number,
    totalCertiCost: number,
    totalPrice: number,
    fileName: string, sheetName: string, selectedRegion: string) {
  
        const rows: (string | number | null)[][] = [
            [translate("Customer"), invoice?.mailName ?? ""],
            [translate("Invoice Number"), invoice?.invoiceNumber ?? ""],
            [translate("Invoice Date"), formatDateWithDash(true, invoice?.invoiceDate?.toString()?.split('T')[0] || "")],
            [translate("Payment Terms"), invoice?.termsDescription ?? ""],
            [],
            [],
            [
                translate("Dana Part Number"),
                translate("Customer PO"),
                translate("Order Number"),
                translate("Customer Part Number"),
                translate("Part Description"),
                translate("Qty Shipped"),
                `${translate("Unit Price")} (${invoice?.currency ?? ""})`,
                `${translate("3.1 Certificate Cost")} (${invoice?.currency ?? ""})`,
                `${translate("Extended Price")} (${invoice?.currency ?? ""})`,
            ],
            ...(invoice?.invoiceInfo?.map(item => [
                item.partNumber,
                item.poNumber,
                invoice?.documentNumber,
                item.customerPartNumber,
                item.partDescription,
                item.quantityShipped,
                formatDecimal(item.pricePerUnit, { locale: "fr-FR" }),
                formatDecimal(item.partPriceInfo?.find(p => p.priceType === "3.1 Certification")?.amount ?? 0, { locale: "fr-FR" }),
                formatDecimal(
                    (item.netAmount ?? 0) +
                    (item.partPriceInfo?.find(p => p.priceType === "Tariff")?.amount ?? 0) *
                    (item.quantityShipped ?? 0) +
                    (item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0) *
                    (item.quantityOrdered ?? 0)
                    , { locale: "fr-FR" }),
            ]) ?? []),
            [],
            [null, null, null, null, null, null, null, `${translate("Item Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalPrice, { locale: "fr-FR" })],
            [null, null, null, null, null, null, null, `${translate("Special Discount")} (${invoice?.currency ?? ""}):`, `-${formatDecimal(totalDiscount, { locale: "fr-FR" })}`],
            [null, null, null, null, null, null, null, `${translate("Total 3.1 Certificate Cost")} (${invoice?.currency ?? ""}):`, formatDecimal(totalCertiCost ?? 0, { locale: "fr-FR" })],
            [null, null, null, null, null, null, null, `${translate("Freight Charges")} (${invoice?.currency ?? ""}):`, formatDecimal(invoice?.freightAmount ?? 0, { locale: "fr-FR" })],
            [null, null, null, null, null, null, null, `${translate("Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalAmt, { locale: "fr-FR" })],
        ];

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Line item numeric columns: D (Qty), E (Unit Price), F (Cert Cost), G (Extended Price)

        ws["!cols"] = [
            { wch: 18 }, // A - Dana Part Number
            { wch: 18 }, // B - Customer PO
            { wch: 18 }, // C - Order Number
            { wch: 26 }, // D - Customer Part Number
            { wch: 40 }, // E - Part Description
            { wch: 12 }, // F - Qty Shipped
            { wch: 16 }, // G - Unit Price
            { wch: 16 }, // H - 3.1 Certificate Cost
            { wch: 16 }, // I - Extended Price
        ];

        const lineItemStartRow = 8; // row 10 = first data row (1-indexed, after 9 header rows)
        invoice?.invoiceInfo?.forEach((_, i) => {
            const rowNum = lineItemStartRow + i;
            applyNumberFormat(ws, `G${rowNum}`);
            applyNumberFormat(ws, `H${rowNum}`);
            applyNumberFormat(ws, `I${rowNum}`);
        });

        // Totals block numeric column (G), 5 rows starting after the blank spacer row
        const totalsStartRow = lineItemStartRow + (invoice?.invoiceInfo?.length ?? 0) + 1;
        for (let i = 0; i < 5; i++) {
            applyNumberFormat(ws, `I${totalsStartRow + i}`);
        }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
}

export const InvoiceExcelDownloadButton = forwardRef<HTMLButtonElement, InvoiceExcelDownloadButtonProps>(
    (
        {
            invoice,
            totalAmt,
            totalCertiCost,
            totalDiscount,
            totalPrice,
            fileName = "invoice",
            sheetName = "Invoice",
            label = "Download Excel",
            size = 28,
            disabled,
            className,
            selectedRegion,
            onBeforeDownload,
            onAfterDownload,
        },
        ref,
    ) => {
        const handleDownload = async () => {
            if (disabled) return;
            if (typeof window === "undefined") return;

            try {
                await onBeforeDownload?.();
            } catch {
                // Swallow to avoid blocking download
            }

            try {
                buildInvoiceWorkbook(invoice ?? null, totalAmt ?? 0, totalDiscount ?? 0, totalCertiCost ?? 0, totalPrice ?? 0, fileName, sheetName, selectedRegion ?? "");
            } finally {
                onAfterDownload?.();
            }
        };

        return (
            <StyledExcelDownloadButton
                ref={ref}
                type="button"
                aria-label={label}
                title={label}
                onClick={handleDownload}
                className={className}
                disabled={disabled}
            >
                <Icon src={XlsDownload} size={size} />
            </StyledExcelDownloadButton>
        );
    },
);

InvoiceExcelDownloadButton.displayName = "InvoiceExcelDownloadButton";